/* eslint-disable prefer-destructuring */
import { precise_round_to_number, sortItemsByOrgAndPriceName, sortVoucherEnabledOrgs } from '../../../../functions'

export default function useOrderItems(
  appliedVouchers,
  organisationsInfo,
  formData,
  getProductDiscount,
  calculateProductPromoCode
) {
  const { organisation, products, isTeamRego } = formData

  const applyVoucherDeduction = (balance, voucherType, voucherCode, voucherId, product) => {
    if (!balance) {
      return {
        product,
        balance,
      }
    }

    let voucherAmount = 0
    let totalPrice = product.totalPrice

    if (balance >= totalPrice) {
      voucherAmount = totalPrice
      totalPrice = 0
      balance = precise_round_to_number(balance - voucherAmount, 2)
    } else {
      voucherAmount = balance
      totalPrice = precise_round_to_number(totalPrice - balance, 2)
      balance = 0
    }

    return {
      product: {
        ...product,
        deductions: (product.deductions || 0) + (voucherAmount || 0),
        voucher: {
          voucherAmount: voucherAmount || null,
          voucherCode: voucherAmount ? voucherCode : null,
          voucherType: voucherAmount ? voucherType : null,
          voucherId: voucherAmount ? voucherId : null,
        },
        totalPrice,
      },
      balance,
    }
  }

  const applyVoucherDeductions = (memberId, items) => {
    // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
    if (!items || !items.length) return items
    const memberVoucherData = appliedVouchers[memberId]

    if (!memberVoucherData) {
      return items
    }

    const filterItems = items.filter(i => i.type !== 'Add-on')
    const sortedItems = sortItemsByOrgAndPriceName(filterItems)
    if (!Object.keys(sortedItems).length) return items
    const orderedOrgs = sortVoucherEnabledOrgs(organisationsInfo, organisation.sfid, memberVoucherData)
    /* eslint-disable-next-line prefer-const */
    const newProducts = []

    let balance = memberVoucherData.balance // start with fresh balance
    const voucherCode = memberVoucherData.appliedVoucher
    const voucherId = memberVoucherData.voucherId
    const voucherType = memberVoucherData.voucherType

    orderedOrgs.forEach(org => {
      if (sortedItems[org.sfid]) {
        sortedItems[org.sfid].forEach(product => {
          /* eslint-disable-next-line prefer-const */
          const response = applyVoucherDeduction(balance, voucherType, voucherCode, voucherId, product)

          newProducts.push(response.product)
          balance = response.balance
        })
      }
    })

    return items.map(item => newProducts.find(p => p.id === item.id) || item)
  }

  const applyDiscountDeductions = (memberId, orderItems) =>
    orderItems.map(orderItem => {
      const { id: productId, totalPrice } = orderItem
      const productDiscount = getProductDiscount(memberId, productId)

      const { discountId, discountName, discountType, subType, discountAmount = 0 } = productDiscount || {}

      return {
        ...orderItem,
        ...(productDiscount && {
          totalPrice: totalPrice - discountAmount,
          deductions: (orderItem.deductions || 0) + (discountAmount || 0),
          discount: {
            discountId,
            discountName,
            discountType,
            subType,
            discountAmount,
          },
        }),
      }
    })

  const applyPromoCodeDeductions = (memberId, orderItems) =>
    orderItems.map(orderItem => {
      const { id: productId, totalPrice } = orderItem

      const productPromoCode = calculateProductPromoCode(memberId, productId, totalPrice)

      const { promoCodeAmount = 0 } = productPromoCode || {}

      return {
        ...orderItem,
        ...(productPromoCode && {
          deductions: (orderItem.deductions || 0) + (promoCodeAmount || 0),
          totalPrice: totalPrice - promoCodeAmount,
          productPromoCode,
        }),
      }
    })

  const applyOrderItemDeductions = (memberId, orderItems = []) => {
    let newOrderItems = orderItems

    newOrderItems = applyDiscountDeductions(memberId, newOrderItems)

    newOrderItems = applyPromoCodeDeductions(memberId, newOrderItems)

    newOrderItems = applyVoucherDeductions(memberId, newOrderItems)

    return newOrderItems
  }

  const orderItems = Object.values(isTeamRego ? [formData.registeredTeam] : formData.registrations).reduce((acc, r) => {
    const memberId = isTeamRego ? 'teamRegistration' : r.sfid

    const mergedProducts = isTeamRego
      ? {
          ...r.teamProducts,
          ...r.teamSubEventProducts,
          ...r.teamAddOn,
        }
      : {
          ...r.newProducts,
          ...r.newSubEventProducts,
          ...r.newAddOnProducts,
        }
    /* eslint-disable-next-line prefer-const */
    const memberOrderItems = Object.keys(mergedProducts || {})
      .filter(id => mergedProducts[id].quantity)
      .map(id => {
        const product = products[id]
        const quantity = Number(mergedProducts[id].quantity)

        const listPrice = Number(product.price || product.amount || 0)
        const totalPrice = precise_round_to_number(listPrice * quantity, 2)

        return {
          id,
          formProductId: product.formProductId,
          name: product.name,
          organisation: product.definedById,
          listPrice,
          quantity,
          totalPrice,
          deductions: 0,
          grantMemberTypes: product.grantMemberTypes || [],
          memberId: memberId || r.sfid || r.id,
          type: product.type,
          gstApplicable: product.gstApplicable,
          isForSubEvent: product.isForSubEvent,
          ...(product.type === 'Add-on' && {
            image: product.imageURL,
            deliveryMethod: product.deliveryMethod,
            sku: product.sku,
            productAttributes: product.productAttributes,
            attributeId: mergedProducts[id].attributeId,
            optionIds: mergedProducts[id].optionIds,
          }),
          ...(product.type === 'Event' &&
            product.subType === 'Event Entry' &&
            mergedProducts[id].attributeValue && {
              productAttributes: product.productAttributes,
              attributeValue: mergedProducts[id].attributeValue,
            }),
        }
      })

    acc.push(...applyOrderItemDeductions(memberId, memberOrderItems))

    return acc
  }, [])

  return orderItems
}
